import { ChevronDown, ExternalLink, Instagram, Search, User } from 'lucide-react'
import Image from 'next/image'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { instagramSearch } from 'sprites-types/workflow/tools/instagramSearch'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'
import { InstagramPostItem } from './InstagramFetchPostsDisplay'

function SearchResultItem({ result, className }: { result: any; className?: string }) {
  const username = result.username || result.user || result.account
  const fullName = result.full_name || result.name || result.displayName
  const bio = result.biography || result.bio || result.description
  const followers = result.followers_count || result.followers || result.follower_count || 0
  const following = result.following_count || result.following || result.following_count || 0
  const posts = result.media_count || result.posts || result.post_count || 0
  const profilePic = result.profile_pic_url || result.avatar || result.profile_image
  const isVerified = result.is_verified || result.verified
  const url = result.external_url || result.website || result.url
  const profileUrl = `https://instagram.com/${username}`

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
    return num.toLocaleString()
  }

  return (
    <div
      className={`rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-sm ${className}`}
    >
      <div className="px-4 py-3">
        <div className="flex items-start gap-3">
          {profilePic ? (
            <Image
              src={profilePic}
              alt={`${username} profile`}
              width={48}
              height={48}
              className="h-12 w-12 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-200">
              <User className="h-6 w-6 text-gray-500" />
            </div>
          )}

          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-2">
              <div className="truncate text-sm font-semibold text-gray-900">
                {fullName || username || 'Instagram User'}
              </div>
              {isVerified && <div className="text-blue-500">✓</div>}
            </div>

            <div className="mb-2 text-xs text-gray-600">@{username}</div>

            {bio && <div className="mb-3 line-clamp-2 text-xs text-gray-700">{bio}</div>}

            <div className="mb-2 flex items-center gap-4 text-xs text-gray-600">
              <div className="flex items-center gap-1">
                <span className="font-medium text-gray-900">{formatNumber(posts)}</span>
                <span>posts</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="font-medium text-gray-900">{formatNumber(followers)}</span>
                <span>followers</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="font-medium text-gray-900">{formatNumber(following)}</span>
                <span>following</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {url && (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                >
                  <ExternalLink className="h-3 w-3" />
                  Website
                </a>
              )}
              <a
                href={profileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-pink-600 hover:text-pink-800"
              >
                <Instagram className="h-3 w-3" />
                View Profile
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function InstagramSearchDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(instagramSearch, nodeExecution.inputs)
  const [isExpanded, setIsExpanded] = useState(true)

  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const query = inputs.query || inputs.search || inputs.q || 'search'
  const results = Array.isArray(result)
    ? result
    : (result as any)?.data ||
      (result as any)?.users ||
      (result as any)?.accounts ||
      (result ? [result] : [])
  const resultType =
    (inputs.resultsType as string | undefined) ||
    (inputs.results_type as string | undefined) ||
    'details'
  const isPostResults =
    ['posts', 'stories', 'mentions'].includes(resultType) ||
    results.some((item: any) => item && (item.media || item.owners))

  if (results.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900">No results found</div>
            <div className="text-xs text-gray-600">Search: &quot;{query}&quot;</div>
          </div>
        </div>
      </div>
    )
  }

  if (isPostResults) {
    if (results.length === 1) {
      return <InstagramPostItem post={results[0]} />
    }

    return (
      <div className="space-y-3">
        <ContextMenuWrapper actions={[]}>
          <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
              aria-expanded={isExpanded}
            >
              <div className="flex-shrink-0">
                <Instagram className="h-5 w-5 text-pink-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-gray-900">Instagram Posts</div>
                <div className="text-xs font-medium tracking-wide text-gray-500">
                  {results.length} posts • &quot;{query}&quot;
                </div>
              </div>
              <div className="flex-shrink-0">
                <ChevronDown
                  className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                />
              </div>
            </button>
          </div>
        </ContextMenuWrapper>

        {isExpanded && (
          <div className="space-y-2">
            {results.map((result: any, index: number) => (
              <InstagramPostItem
                key={result.id || result.permalink || index}
                post={result}
                className="mx-8"
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  if (results.length === 1) {
    return <SearchResultItem result={results[0]} />
  }

  return (
    <div className="space-y-3">
      <ContextMenuWrapper actions={[]}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <Instagram className="h-5 w-5 text-pink-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">
                Instagram Search Results
              </div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {results.length} accounts found • &quot;{query}&quot;
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {isExpanded && (
        <div className="space-y-2">
          {results.map((result: any, index: number) => (
            <SearchResultItem
              key={result.id || result.username || index}
              result={result}
              className="mx-8"
            />
          ))}
        </div>
      )}
    </div>
  )
}

function downloadInstagramSearch(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function instagramSearchToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(instagramSearch, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const query = inputs.query || inputs.search || inputs.q || 'search'
  const results = Array.isArray(result)
    ? result
    : (result as any)?.data ||
      (result as any)?.users ||
      (result as any)?.accounts ||
      (result ? [result] : [])

  if (results.length === 0) {
    return `## Instagram Search Results

**Query:** "${query}"

No results found.

---
`
  }

  const resultsMarkdown = results
    .map((result: any) => {
      const username = result.username || result.user || result.account
      const fullName = result.full_name || result.name || result.displayName
      const bio = result.biography || result.bio || result.description
      const followers = result.followers_count || result.followers || result.follower_count || 0
      const following = result.following_count || result.following || result.following_count || 0
      const posts = result.media_count || result.posts || result.post_count || 0
      const url = result.external_url || result.website || result.url

      return `### ${fullName || username}

**Username:** @${username}
**Followers:** ${followers.toLocaleString()}
**Following:** ${following.toLocaleString()}
**Posts:** ${posts.toLocaleString()}
${bio ? `**Bio:** ${bio}` : ''}
${url ? `**Website:** ${url}` : ''}
**Profile:** https://instagram.com/${username}
`
    })
    .join('\n---\n\n')

  return `## Instagram Search Results (${results.length} accounts)

**Query:** "${query}"

${resultsMarkdown}

---
`
}

export const InstagramSearchDisplay = createWorkflowToolComponent(
  InstagramSearchDisplayComponent,
  downloadInstagramSearch,
  instagramSearchToMarkdown
)
